import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Divider,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { ExpandLess, ExpandMore, Straighten, Add, Remove, InfoOutlined } from '@mui/icons-material';

/**
 * Ce composant propose des champs texte pour filtrer les mesures.
 * L'accordéon est replié par défaut (expanded=false).
 * Chaque champ filtre les joueurs ayant une mesure >= la valeur saisie.
 * Si isMobile est true, le composant n'est pas rendu. 
 */
const PlayerFiltersMedidas = ({
  isMobile,
  alturaFilter,
  setAlturaFilter,
  envergaduraFilter,
  setEnvergaduraFilter,
  alcanceFilter,
  setAlcanceFilter,
  saltoVerticalMaxFilter,
  setSaltoVerticalMaxFilter,
  saltoVerticalPieFilter,
  setSaltoVerticalPieFilter,
  medidasPesoFilter,
  setMedidasPesoFilter,
  grasaCorporalFilter,
  setGrasaCorporalFilter,
  longitudManoFilter,
  setLongitudManoFilter,
  anchoManoFilter,
  setAnchoManoFilter,
  agilidadFilter,
  setAgilidadFilter,
  sprintCuartoFilter,
  setSprintCuartoFilter,
  forceExpanded = false
}) => {
  // ⚠️ TOUS LES HOOKS EN PREMIER - AVANT TOUTE CONDITION
  const { t } = useTranslation();
  // Pour replier/déplier l'accordéon (appel du hook toujours exécuté pour respecter les règles de React)
  const [expanded, setExpanded] = useState(forceExpanded || false);

  // État pour gérer l'affichage de l'icône info au survol
  const [hoveredField, setHoveredField] = useState(null);

  // Refs pour gérer l'incrémentation/décrémentation continue
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Mise à jour de l'état expanded quand forceExpanded change
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setExpanded(forceExpanded);
    }
  }, [forceExpanded]);

  // Cleanup des intervals au démontage du composant
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Configuration des métriques avec descriptions complètes
  const metrics = [
    {
      key: 'altura',
      translationKey: 'altura',
      unit: 'cm',
      step: 1,
      value: alturaFilter,
      setter: setAlturaFilter
    },
    {
      key: 'envergadura',
      translationKey: 'envergadura',
      unit: 'cm',
      step: 1,
      value: envergaduraFilter,
      setter: setEnvergaduraFilter
    },
    {
      key: 'alcance',
      translationKey: 'alcance',
      unit: 'cm',
      step: 1,
      value: alcanceFilter,
      setter: setAlcanceFilter
    },
    {
      key: 'salto_vertical_maximo',
      translationKey: 'saltoVerticalMax',
      unit: 'cm',
      step: 1,
      value: saltoVerticalMaxFilter,
      setter: setSaltoVerticalMaxFilter
    },
    {
      key: 'salto_vertical_de_pie',
      translationKey: 'saltoVerticalPie',
      unit: 'cm',
      step: 1,
      value: saltoVerticalPieFilter,
      setter: setSaltoVerticalPieFilter
    },
    {
      key: 'peso',
      translationKey: 'peso',
      unit: 'kg',
      step: 0.5,
      value: medidasPesoFilter,
      setter: setMedidasPesoFilter
    },
    {
      key: 'grasa_corporal',
      translationKey: 'grasaCorporal',
      unit: '%',
      step: 0.1,
      value: grasaCorporalFilter,
      setter: setGrasaCorporalFilter
    },
    {
      key: 'longitud_mano',
      translationKey: 'longitudMano',
      unit: 'cm',
      step: 0.1,
      value: longitudManoFilter,
      setter: setLongitudManoFilter
    },
    {
      key: 'ancho_mano',
      translationKey: 'anchoMano',
      unit: 'cm',
      step: 0.1,
      value: anchoManoFilter,
      setter: setAnchoManoFilter
    },
    {
      key: 'agilidad',
      translationKey: 'agilidad',
      unit: 's',
      step: 0.01,
      value: agilidadFilter,
      setter: setAgilidadFilter
    },
    {
      key: 'sprint_cuarto',
      translationKey: 'sprintCuarto',
      unit: 's',
      step: 0.01,
      value: sprintCuartoFilter,
      setter: setSprintCuartoFilter
    }
  ];

  // Fonctions pour incrémenter/décrémenter avec gestion des décimales
  const incrementValue = (value, setter, step = 1) => {
    const numValue = parseFloat(value) || 0;
    const newValue = numValue + step;
    // Déterminer le nombre de décimales basé sur le step
    const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
    const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    setter(roundedValue.toString());
  };

  const decrementValue = (value, setter, step = 1) => {
    const numValue = parseFloat(value) || 0;
    const newValue = Math.max(0, numValue - step);
    // Déterminer le nombre de décimales basé sur le step
    const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
    const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    setter(roundedValue.toString());
  };

  // Fonctions pour gérer l'appui continu sur les boutons
  const startIncrement = (value, setter, step) => {
    // Premier incrément immédiat
    incrementValue(value, setter, step);

    // Après un délai de 500ms, commencer l'incrémentation continue
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setter(prev => {
          const numValue = parseFloat(prev) || 0;
          const newValue = numValue + step;
          const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
          const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
          return roundedValue.toString();
        });
      }, 100); // Répéter toutes les 100ms
    }, 500);
  };

  const startDecrement = (value, setter, step) => {
    // Premier décrément immédiat
    decrementValue(value, setter, step);

    // Après un délai de 500ms, commencer la décrémentation continue
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setter(prev => {
          const numValue = parseFloat(prev) || 0;
          const newValue = Math.max(0, numValue - step);
          const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
          const roundedValue = Math.round(newValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
          return roundedValue.toString();
        });
      }, 100); // Répéter toutes les 100ms
    }, 500);
  };

  const stopContinuous = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Fonction pour gérer les changements d'input
  const handleInputChange = (setValue, key) => (e) => {
    const inputValue = e.target.value;
    
    // Pour le champ grasa_corporal, limiter à un seul chiffre après la virgule
    if (key === 'grasa_corporal') {
      if (inputValue === '' || /^\d*\.?\d{0,1}$/.test(inputValue)) {
        setValue(inputValue);
      }
    } else {
      // Pour les autres champs, permettre les nombres avec décimales
      if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
        setValue(inputValue);
      }
    }
  };

  // ⚠️ MAINTENANT ON PEUT FAIRE LES CONDITIONS ET RETURNS
  // Si affichage mobile, ne pas rendre le composant 
  if (isMobile) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {/* En-tête avec icône et bouton pour replier/déplier (seulement si pas forceExpanded) */}
      {!forceExpanded && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mb: 2,
              p: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Straighten sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {t('playerFiltersMedidas.title')}
            </Typography>
            <IconButton size="small">
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* Contenu repliable/dépliable */}
      <Collapse in={expanded}>
        {/* Grille responsive : 6 colonnes sur large, 3 sur moyen, 1 sur mobile */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 2,
            '@media (max-width: 1200px)': {
              gridTemplateColumns: 'repeat(3, 1fr)',
            },
            '@media (max-width: 600px)': {
              gridTemplateColumns: 'repeat(1, 1fr)',
            },
          }}
        >
          {metrics.map((metric) => (
            <Box
              key={metric.key}
              onMouseEnter={() => setHoveredField(metric.key)}
              onMouseLeave={() => setHoveredField(null)}
            >
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      {t(`playerFiltersMedidas.metrics.${metric.translationKey}.category`)}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      {t(`playerFiltersMedidas.metrics.${metric.translationKey}.label`)} {metric.unit ? `(${metric.unit})` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mb: 0.5 }}>
                      {t('playerFiltersMedidas.tooltipExample')} {t(`playerFiltersMedidas.metrics.${metric.translationKey}.example`)}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      ➤ {t(`playerFiltersMedidas.metrics.${metric.translationKey}.description`)}
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
              >
                <TextField
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {t(`playerFiltersMedidas.metrics.${metric.translationKey}.label`)} ({metric.unit})
                      <InfoOutlined 
                        sx={{ 
                          fontSize: 14, 
                          color: 'text.secondary',
                          opacity: hoveredField === metric.key ? 1 : 0,
                          transition: 'opacity 0.2s'
                        }} 
                      />
                    </Box>
                  }
                  variant="outlined"
                  value={metric.value}
                  onChange={handleInputChange(metric.setter, metric.key)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          onMouseDown={() => startDecrement(metric.value, metric.setter, metric.step)}
                          onMouseUp={stopContinuous}
                          onMouseLeave={stopContinuous}
                          onTouchStart={() => startDecrement(metric.value, metric.setter, metric.step)}
                          onTouchEnd={stopContinuous}
                          size="small"
                          sx={{
                            backgroundColor: '#5DADE2',
                            color: 'white',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            padding: 0,
                            marginLeft: 0.5,
                            marginRight: -0.5,
                            '&:hover': {
                              backgroundColor: '#4A9BCE',
                              color: 'white'
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 18
                            }
                          }}
                        >
                          <Remove />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onMouseDown={() => startIncrement(metric.value, metric.setter, metric.step)}
                          onMouseUp={stopContinuous}
                          onMouseLeave={stopContinuous}
                          onTouchStart={() => startIncrement(metric.value, metric.setter, metric.step)}
                          onTouchEnd={stopContinuous}
                          size="small"
                          sx={{
                            backgroundColor: '#3B5998',
                            color: 'white',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            padding: 0,
                            marginRight: 0.5,
                            marginLeft: -0.5,
                            '&:hover': {
                              backgroundColor: '#2D4373',
                              color: 'white'
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 18
                            }
                          }}
                        >
                          <Add />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                    }
                  }}
                />
              </Tooltip>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PlayerFiltersMedidas;